import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Calculator, DollarSign, TrendingUp, AlertTriangle, Target } from "lucide-react";
import { BUSINESS_SECTORS } from "@/utils/businessSectors";

interface BusinessData {
  sector: string;
  revenue: number;
  ebitda: number;
  age: number;
  employees: number;
  location: string;
  assets: string;
  growthTrend: string;
  marketPosition: string;
}

interface ValuationResult {
  valuation: {
    valuationRange: {
      min: number;
      max: number;
      recommended: number;
    };
    methodology: string[];
    keyFactors: string[];
    riskFactors: string[];
    industryAnalysis: string;
    recommendations: string[];
    confidence: number;
    multiples: {
      revenue: number;
      ebitda: number;
    };
  };
  industryBenchmarks: {
    revenueMultiple: number;
    ebitdaMultiple: number;
  };
  marketData: {
    comparableCount: number;
    sector: string;
  };
}

export function AIBusinessValuation() {
  const [businessData, setBusinessData] = useState<BusinessData>({
    sector: '',
    revenue: 0,
    ebitda: 0,
    age: 0,
    employees: 0,
    location: '',
    assets: '',
    growthTrend: '',
    marketPosition: ''
  });
  const [result, setResult] = useState<ValuationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleValueBusiness = async () => {
    if (!businessData.sector || !businessData.revenue) {
      toast({
        title: "Error",
        description: "Please enter at least sector and revenue",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('ai-business-valuation', {
        body: { businessData }
      });

      if (error) throw error;
      setResult(data);
      
      toast({
        title: "Valuation Complete",
        description: `Recommended value: $${data.valuation.valuationRange.recommended.toLocaleString()}`
      });
    } catch (error) {
      console.error('Error valuing business:', error);
      toast({
        title: "Error",
        description: "Failed to generate business valuation",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return "text-green-600 bg-green-50";
    if (confidence >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const sectors = [
    "Technology", "Healthcare", "Retail", "Manufacturing", "Hospitality",
    "Professional Services", "Construction", "Food & Beverage", "Transport",
    "Education", "Real Estate", "Financial Services"
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Calculator className="h-6 w-6" />
          AI Business Valuation
        </h2>
        <p className="text-muted-foreground">
          Get AI-powered business valuations based on market data and industry benchmarks
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>Enter details about the business to be valued</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="sector">Sector</Label>
                <Select value={businessData.sector} onValueChange={(value) => 
                  setBusinessData(prev => ({ ...prev, sector: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select business sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_SECTORS.map(sector => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="revenue">Annual Revenue ($)</Label>
                  <Input
                    id="revenue"
                    type="number"
                    value={businessData.revenue || ''}
                    onChange={(e) => setBusinessData(prev => ({
                      ...prev,
                      revenue: parseInt(e.target.value) || 0
                    }))}
                    placeholder="1000000"
                  />
                </div>

                <div>
                  <Label htmlFor="ebitda">EBITDA ($)</Label>
                  <Input
                    id="ebitda"
                    type="number"
                    value={businessData.ebitda || ''}
                    onChange={(e) => setBusinessData(prev => ({
                      ...prev,
                      ebitda: parseInt(e.target.value) || 0
                    }))}
                    placeholder="200000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="age">Business Age (years)</Label>
                  <Input
                    id="age"
                    type="number"
                    value={businessData.age || ''}
                    onChange={(e) => setBusinessData(prev => ({
                      ...prev,
                      age: parseInt(e.target.value) || 0
                    }))}
                    placeholder="5"
                  />
                </div>

                <div>
                  <Label htmlFor="employees">Number of Employees</Label>
                  <Input
                    id="employees"
                    type="number"
                    value={businessData.employees || ''}
                    onChange={(e) => setBusinessData(prev => ({
                      ...prev,
                      employees: parseInt(e.target.value) || 0
                    }))}
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={businessData.location}
                  onChange={(e) => setBusinessData(prev => ({
                    ...prev,
                    location: e.target.value
                  }))}
                  placeholder="Sydney, NSW"
                />
              </div>

              <div>
                <Label htmlFor="assets">Key Assets</Label>
                <Textarea
                  id="assets"
                  value={businessData.assets}
                  onChange={(e) => setBusinessData(prev => ({
                    ...prev,
                    assets: e.target.value
                  }))}
                  placeholder="Equipment, inventory, intellectual property..."
                  className="min-h-[80px]"
                />
              </div>

              <div>
                <Label htmlFor="growthTrend">Growth Trend</Label>
                <Select value={businessData.growthTrend} onValueChange={(value) => 
                  setBusinessData(prev => ({ ...prev, growthTrend: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select growth trend" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="declining">Declining</SelectItem>
                    <SelectItem value="stable">Stable</SelectItem>
                    <SelectItem value="growing">Growing</SelectItem>
                    <SelectItem value="rapid-growth">Rapid Growth</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="marketPosition">Market Position</Label>
                <Select value={businessData.marketPosition} onValueChange={(value) => 
                  setBusinessData(prev => ({ ...prev, marketPosition: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select market position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="market-leader">Market Leader</SelectItem>
                    <SelectItem value="strong-competitor">Strong Competitor</SelectItem>
                    <SelectItem value="niche-player">Niche Player</SelectItem>
                    <SelectItem value="emerging">Emerging</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleValueBusiness} 
                disabled={loading}
                className="w-full"
              >
                {loading ? "Generating Valuation..." : "Generate AI Valuation"}
              </Button>
            </CardContent>
          </Card>
        </div>

        {result && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Valuation Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Minimum</p>
                    <p className="font-semibold">{formatCurrency(result.valuation.valuationRange.min)}</p>
                  </div>
                  <div className="p-3 bg-primary/10 rounded-lg border-2 border-primary">
                    <p className="text-sm text-muted-foreground">Recommended</p>
                    <p className="font-bold text-lg">{formatCurrency(result.valuation.valuationRange.recommended)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Maximum</p>
                    <p className="font-semibold">{formatCurrency(result.valuation.valuationRange.max)}</p>
                  </div>
                </div>

                <div className={`p-3 rounded-lg ${getConfidenceColor(result.valuation.confidence)}`}>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    <span className="font-semibold">Confidence: {result.valuation.confidence}%</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Valuation Multiples</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-2 bg-muted rounded">
                      <p className="text-sm text-muted-foreground">Revenue Multiple</p>
                      <p className="font-semibold">{result.valuation.multiples.revenue.toFixed(1)}x</p>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <p className="text-sm text-muted-foreground">EBITDA Multiple</p>
                      <p className="font-semibold">{result.valuation.multiples.ebitda.toFixed(1)}x</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Methodology Used</h4>
                  <div className="flex flex-wrap gap-1">
                    {result.valuation.methodology.map((method, index) => (
                      <Badge key={index} variant="outline">{method}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Analysis Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Key Value Drivers</h4>
                  <ul className="space-y-1">
                    {result.valuation.keyFactors.map((factor, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-green-600 mt-1">•</span>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Risk Factors
                  </h4>
                  <ul className="space-y-1">
                    {result.valuation.riskFactors.map((risk, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-red-600 mt-1">•</span>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Industry Analysis</h4>
                  <p className="text-sm text-muted-foreground">{result.valuation.industryAnalysis}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Recommendations</h4>
                  <ul className="space-y-1">
                    {result.valuation.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Market data based on {result.marketData.comparableCount} comparable businesses in {result.marketData.sector}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}